import * as React from "react";

import { RouteComponentProps } from "react-router-dom";

import {
  AccessReportsPrintOptions,
  AccessReportsWizardElement,
  AccessReportsWizardModel,
  AccessReportsWizardOption,
  AccessReportsWizardProduct,
  AccessReportsWizardTemplate,
  AccessReportsWizardVersionModel
} from "@app/access-reports/shared";
import {
  AccessReportsAdminCoverPageTab,
  AccessReportsAdminPrintSettingsTab,
  AccessReportsAdminProductsTab,
  AccessReportsAdminDashboardTabEnum,
  AccessReportsAdminDashboardModalTypeEnum,
  AccessReportsAdminVersionTypeEnum
} from "@app/access-reports/admin";
import { formatDateCustom, LoadingSpinner, Page, PageInner, PageSection } from "@app/shared";
import { QESAlert, QESButton, QESButtonTypes, QESRadioButton, QESRadioGroup } from "@app/shared/qes-standards";
import { isEqual } from "lodash";
import { Modal } from "@app/shared/Modals";
import { JSX } from "react";

type AccessReportsAdminDashboardProps = RouteComponentProps<unknown> & {
  accessReportsWizard: AccessReportsWizardModel;
  checkedOutUsername: string;
  disableUserActions: boolean;
  isCheckedOutByCurrentUser: boolean;
  loading: boolean
  onActivateClick: () => void;
  onCheckInClick: () => void;
  onCheckOutClick: () => void;
  onCoverPageDefaultValueChange: (elementId: string, event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => void;
  onCoverPageToggle: (elementId: string, key: keyof AccessReportsWizardElement) => void;
  onCreateNewVersionClick: () => void;
  onDeleteVersionClick: () => void;
  onPrintOptionsChange: (formGroupId: number, key: keyof AccessReportsPrintOptions, defaultValue: string | boolean) => void;
  onRemoveProductsClick: (products: AccessReportsWizardProduct[]) => void;
  onResetPrintSettingsClick: () => void;
  onSelectVersionTypeClick: (selectedVersion: AccessReportsAdminVersionTypeEnum) => void;
  onSaveClick: () => void;
  onSaveLogosClick: (logos: AccessReportsWizardOption[], reloadPage: boolean) => void;
  onSaveProductsClick: (productTypes: AccessReportsWizardOption[]) => void;
  onSaveProductTypesClick: (productTypes: AccessReportsWizardOption[]) => void;
  selectedVersion: AccessReportsWizardVersionModel;
  selectedVersionType: AccessReportsAdminVersionTypeEnum;
}

interface AccessReportsAdminDashboardState {
  modalType: AccessReportsAdminDashboardModalTypeEnum;
}

class AccessReportsAdminDashboard extends React.Component<AccessReportsAdminDashboardProps, AccessReportsAdminDashboardState> {
  private deleteButtonRef = React.createRef<HTMLButtonElement>();
  private activateButtonRef = React.createRef<HTMLButtonElement>();

  constructor(props: AccessReportsAdminDashboardProps) {
    super(props);

    this.state = {
      modalType: null,
    };
  }

  public render() {
    return (
      <Page className="bg-white" documentTitle="Access Reports Admin">
        <PageInner>
          <PageSection>
            {!!this.state.modalType &&
              this.getModal()
            }

            <div className="row">
              <div className="col-12">
                <h2 className="mt-3 mb-3 header-l font-weight-bold">
                  Access Report Admin
                </h2>
              </div>
            </div>

            {!this.props.loading ? (
              this.getDashboardBody()
            ) : (
              <div className="row">
                <div className="col-12">
                  <div className="text-center">
                    <LoadingSpinner />
                  </div>
                </div>
              </div>
            )}
          </PageSection>
        </PageInner>
      </Page>
    );
  }

  private disableActivation = (): boolean => {
    // compare the currently-activated version with the currently-SAVED in-progress version
    const currentlyActivatedTemplate: AccessReportsWizardTemplate = this.props.accessReportsWizard.accessReportWizardVersions.find(x => !!x.dateActivated)?.template;
    const inProgressTemplate: AccessReportsWizardTemplate = this.props.accessReportsWizard.accessReportWizardVersions.find(x => !x.dateActivated)?.template;
    const equalTemplates: boolean = isEqual(currentlyActivatedTemplate, inProgressTemplate);

    return equalTemplates;
  }

  private getModal = (): JSX.Element => {
    let modalContent: JSX.Element;
    let confirmButtonType: QESButtonTypes;
    let triggerElement: React.RefObject<HTMLButtonElement>;

    switch (this.state.modalType) {
      case AccessReportsAdminDashboardModalTypeEnum.ActivateVersion:
        modalContent = (
          <>
            <h4 className="header-s">Are you sure you want to activate the current version?</h4>
            <p className="copy-text-default">
              This version will be utilized in the access reports wizard.
              <br />
              The previously-activated version will be archived.
            </p>
          </>
        );
        confirmButtonType = "primary";
        triggerElement = this.activateButtonRef;

        break;
      case AccessReportsAdminDashboardModalTypeEnum.DeleteVersion:
        modalContent = (
          <>
            <h4 className="header-s">Are you sure you want to delete the in-progress version?</h4>
            <p className="copy-text-default">
              You will <strong>never</strong> be able to retrieve this version&apos;s template again and all changes will be lost.
            </p>
          </>
        );
        confirmButtonType = "primary-danger";
        triggerElement = this.deleteButtonRef;

        break;
      default:
        console.error(`Unable to retrieve modal content for ${this.state.modalType}`);
        break;
    }

    return (
      <Modal
        isVisible={!!this.state.modalType}
        closeModal={() => this.onHideModalClick(false)}
        content={{
          body: modalContent,
          cancelButtonText: "Cancel",
          confirmButtonAction: () => this.onHideModalClick(true),
          confirmButtonText: "Confirm",
          confirmButtonType: confirmButtonType,
          title: "Warning",
        }}
        hideBodyScroll={true}
        triggerElement={triggerElement}
      />
    );
  }

  private getDashboardBody = (): JSX.Element => {
    if (!this.props.accessReportsWizard || !this.props.accessReportsWizard.accessReportWizardVersions.length) {
      console.error("Unable to retrieve a valid access reports wizard");
    }

    const activatedVersion: AccessReportsWizardVersionModel = this.props.accessReportsWizard.accessReportWizardVersions.find(x => !x.checkedOutDate && !!x.dateActivated);

    // the readonly currently-activated version should always be available to the user
    // if it does not exist, throw an error
    if (!activatedVersion) {
      console.error("Unable to retrieve the currently-activated version of the access reports wizard");
    }

    return (
      <React.Fragment>
        {this.getVersionSelectionSection()}

        {!!this.props.selectedVersionType &&
          this.getSelectedVersionTypeSection()
        }
      </React.Fragment>
    );
  }

  private getQESAlertMessage = (): JSX.Element => {
    if (!!this.props.selectedVersion) {
      switch (this.props.selectedVersionType) {
        case AccessReportsAdminVersionTypeEnum.CurrentVersion:
          return (
            <>
              You are viewing the currently-activated access reports wizard version in <strong>readonly mode</strong>.
              <br />
              In order to make modifications, select the in-progress version and see if it is available for check-out.
            </>
          );
        case AccessReportsAdminVersionTypeEnum.InProgressVersion:
          if (!!this.props.selectedVersion.checkedOutDate) {
            if (this.props.isCheckedOutByCurrentUser) {
              // checked out by current user
              return (
                <>
                  You have <strong>checked-out</strong> the currently in-progress version of the access reports wizard.
                  <br />
                  Other users may not edit this version until you have checked it in.
                </>
              );
            } else {
              // not checked out by current user
              return (
                <>
                  The in-progress version of the access reports wizard is currently checked-out by {this.props.checkedOutUsername}.
                  <br />
                  At the moment, you may only view this version in <strong>readonly mode</strong>.
                </>
              );
            }
          } else {
            // not checked out
            return (
              <>
                You are viewing the in-progress version of the access reports wizard in <strong>readonly mode</strong>.
                <br />
                In order to make modifications, please check-out the template.
              </>
            );
          }
        default:
          console.error(`Unable to retrieve the QES Alert Message based on the selected version: ${this.props.selectedVersion?.accessReportWizardVersionId}`);
      }
    } else {
      return (
        <>
          An in-progress version of the access reports does not currently exist.
          <br />
          If you would like to create one, click on the &quot;create new version&quot; button.
        </>
      );
    }
  }

  private getSelectedVersionTypeSection = (): JSX.Element => {
    return (
      <React.Fragment>
        <div className="row">
          <div className="col-12">
            <QESAlert classNames="mb-3" type="warning" persist={true}>
              <p className="mb-0">
                {this.getQESAlertMessage()}
              </p>
            </QESAlert>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between mb-3">
              <label className="align-self-center mb-0 copy-text-default">
                {this.props.selectedVersion && (
                  <>
                    Date last updated:
                    &nbsp;
                    {this.props.selectedVersion.dateLastUpdated ?
                      formatDateCustom(this.props.selectedVersion.dateLastUpdated, "MMM DD YYYY h:mm:ss A") :
                      "--"
                    }
                  </>
                )}
              </label>

              {(this.props.selectedVersionType !== AccessReportsAdminVersionTypeEnum.CurrentVersion) &&
                this.getVersionActionButtons()
              }
            </div>
          </div>
        </div>

        {!!this.props.selectedVersion && (
          <div className="row">
            <div className="col-12">
              <ul
                className="nav nav-tabs"
                id="access-reports-admin__dashboard--tabs"
              >
                {Object.values(AccessReportsAdminDashboardTabEnum).map((tab, index) => (
                  <li className="nav-item" key={index}>
                    <a
                      className={tab === AccessReportsAdminDashboardTabEnum.Products ? "nav-link active" : "nav-link"}
                      id={`access-reports-admin__dashboard--tab-${index}`}
                      data-toggle="tab"
                      href={`#${this.getTabId(tab)}`}
                    >
                      {tab}
                    </a>
                  </li>
                ))}
              </ul>

              <div className="tab-content">
                <AccessReportsAdminProductsTab
                  accessReportsWizardTemplate={this.props.selectedVersion?.template}
                  disableUserActions={this.props.disableUserActions}
                  onRemoveProductsClick={this.props.onRemoveProductsClick}
                  onSaveProductsClick={this.props.onSaveProductsClick}
                  onSaveProductTypesClick={this.props.onSaveProductTypesClick}
                  selectedVersionType={this.props.selectedVersionType}
                />

                <AccessReportsAdminCoverPageTab
                  accessReportsWizard={this.props.accessReportsWizard}
                  disableUserActions={this.props.disableUserActions}
                  onCoverPageDefaultValueChange={this.props.onCoverPageDefaultValueChange}
                  onCoverPageToggle={this.props.onCoverPageToggle}
                  onSaveClick={this.props.onSaveClick}
                  onSaveLogosClick={this.props.onSaveLogosClick}
                  selectedVersion={this.props.selectedVersion}
                />

                <AccessReportsAdminPrintSettingsTab
                  accessReportsWizard={this.props.accessReportsWizard}
                  disableUserActions={this.props.disableUserActions}
                  onPrintOptionsChange={this.props.onPrintOptionsChange}
                  onResetPrintSettingsClick={this.props.onResetPrintSettingsClick}
                  onSaveClick={this.props.onSaveClick}
                  selectedVersion={this.props.selectedVersion}
                />
              </div>
            </div>
          </div>
        )}
      </React.Fragment>
    );
  }

  private getTabId = (tab: AccessReportsAdminDashboardTabEnum): string => {
    const formattedTab: string[] = tab.split(/(\s+)/).filter((x) => {
      return x.trim().length > 0;
    });

    return formattedTab.join("-").toLowerCase();
  }

  private getVersionSelectionSection = (): JSX.Element => {
    const getVersionTypeDescription = (versionType: AccessReportsAdminVersionTypeEnum): string => {
      switch (versionType) {
        case AccessReportsAdminVersionTypeEnum.CurrentVersion:
          return "The activated version of the template currently utilized by the access reports wizard";
        case AccessReportsAdminVersionTypeEnum.InProgressVersion:
          return "The not-yet activated version of the template that is currently being worked on";
        default:
          console.error(`Unable to get description for version type ${versionType}`);
          return "";
      }
    }

    return (
      <>
        <div className="row">
          <div className="col-md-2">
            <label
              className="label-s"
            >
              Select a version
            </label>
          </div>
          {Object.values(AccessReportsAdminVersionTypeEnum).map((versionType, index) => (
            <div className="col-md-3" key={index}>
              <QESRadioGroup renderInline={true} groupContainerClass="mb-2">
                <QESRadioButton
                  label={versionType}
                  labelClass="label-s"
                  descriptiveText={getVersionTypeDescription(versionType)}
                  checked={this.props.selectedVersionType === versionType}
                  className="form-check-input qes-radio"
                  onChange={() => this.props.onSelectVersionTypeClick(versionType)}
                  testId={`access-reports-admin__dashboard--version-type-${index}`}
                  name="access-reports-version-type"
                  value={versionType}
                />
              </QESRadioGroup>
            </div>
          ))}
        </div>
      </>
    );
  }

  private getVersionActionButtons = (): JSX.Element => {
    if (!!this.props.selectedVersion) {
      if (this.props.isCheckedOutByCurrentUser) {
        return (
          <div className="align-self-center">
            <QESButton
              qesType="primary-danger"
              qesTestId={"access-reports-admin__dashboard--delete"}
              onClick={() => this.showModal(AccessReportsAdminDashboardModalTypeEnum.DeleteVersion)}
              qesRef={this.deleteButtonRef}
            >
              Delete
            </QESButton>

            <QESButton
              className="ml-3"
              disabled={this.disableActivation()}
              qesType="primary"
              qesTestId={"access-reports-admin__dashboard--activate"}
              onClick={() => this.showModal(AccessReportsAdminDashboardModalTypeEnum.ActivateVersion)}
              qesRef={this.activateButtonRef}
            >
              Activate
            </QESButton>

            <QESButton
              className="ml-3"
              qesType="primary"
              qesTestId={"access-reports-admin__dashboard--check-in"}
              onClick={() => this.props.onCheckInClick()}
            >
              Check In
            </QESButton>
          </div>
        );
      } else {
        return (
          <QESButton
            className="align-self-center"
            disabled={!!this.props.selectedVersion.checkedOutDate}
            qesType="primary"
            qesTestId={"access-reports-admin__dashboard--check-out"}
            onClick={() => this.props.onCheckOutClick()}
          >
            Check Out
          </QESButton>
        );
      }
    } else {
      return (
        <QESButton
          className="align-self-center"
          qesType="primary"
          qesTestId={"access-reports-admin__dashboard--create-new-version"}
          onClick={() => this.props.onCreateNewVersionClick()}
        >
          Create New Version
        </QESButton>
      );
    }
  }

  private onHideModalClick = (confirmed: boolean): void => {
    if (confirmed) {
      switch (this.state.modalType) {
        case AccessReportsAdminDashboardModalTypeEnum.ActivateVersion:
          this.props.onActivateClick();
          break;
        case AccessReportsAdminDashboardModalTypeEnum.DeleteVersion:
          this.props.onDeleteVersionClick();
          break;
        default:
          console.error(`Unable to perform modal confirmation action for ${this.state.modalType}`);
          break;
      }
    }

    this.setState({
      modalType: null,
    });
  }

  private showModal = (modalType: AccessReportsAdminDashboardModalTypeEnum): void => {
    this.setState({
      modalType: modalType,
    });
  }
}

export { AccessReportsAdminDashboard };
